import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { apiClient } from "./api";
import { findingViewSchema } from "./api-evidence-finding";
import { textFindingsSchema } from "./customer-text";
import { publicApiBase } from "./docs-origin";

const jsonObject = z.record(z.string(), z.unknown());
// add-service-names-and-groups spec, "Administrators can rename resource
// groups for readers": `resource_group_admin` carries the admin-only raw
// values (the override and the spec's own captured name) behind the
// effective `resource_group_label` — present only when the caller is an
// administrator, absent (not merely null) for every other reader. It is
// `.nullable()` in addition to `.optional()` because an admin payload can in
// principle carry an explicit `null` for an operation with no resource group
// at all (`resource_group` itself null) — a genuine value, not a leak.
const resourceGroupAdminSchema = z
  .object({ override: z.string().nullable(), spec_name: z.string().nullable() })
  .nullable()
  .optional();
// api_reference spec, "Administrators see where a service's reference came from
// and whether its branch moved ahead": the stored result of the latest freshness
// check, administrator responses only. Like every schema in this module it
// ignores a key it does not declare, so a field the server adds cannot blank the
// administrator's catalogue; `tests/api-reference/freshness-contract.test.ts`
// compares it with the backend model so the new field is a red build instead.
export const serviceFreshnessSchema = z.object({
  spec_version_id: z.guid(),
  spec_commit_sha: z.string().nullable(),
  generated_at: z.string().nullable(),
  branch: z.string().nullable(),
  // `source`: named by the service's own metadata; `prod`: the conventional
  // production branch, assumed; `default`: the repository's default branch,
  // assumed and not deployed.
  branch_kind: z.enum(["source", "prod", "default"]).nullable(),
  state: z.enum(["unchecked", "current", "behind", "informational"]),
  head_sha: z.string().nullable(),
  head_committed_at: z.string().nullable(),
  comparison: z.enum(["identical", "ahead", "behind", "diverged"]).nullable(),
  commits_ahead: z.number().int().nullable(),
  code_changed: z.boolean().nullable(),
  checked_at: z.string().nullable(),
  // A short code, never shown as such: only the time of the failed attempt is.
  last_error: z.string().nullable(),
  last_error_at: z.string().nullable(),
});
export type ServiceFreshness = z.infer<typeof serviceFreshnessSchema>;
// Absent (not merely null) from every non-administrator payload and from an
// older server.
const freshnessField = serviceFreshnessSchema.nullable().optional();
export const operationSummarySchema = z.object({
  service_id: z.guid(),
  operation_id: z.string(),
  http_method: z.string(),
  path: z.string(),
  resource_group: z.string().nullable(),
  // The effective label: an administrator's override, else the current
  // spec's own name for the group, else the identifier itself. Null exactly
  // when `resource_group` itself is null.
  resource_group_label: z.string().nullable(),
  resource_group_admin: resourceGroupAdminSchema,
  summary: z.string().nullable(),
  is_public: z.boolean(),
  // make-public-docs-customer-ready, design §5: the findings of the operation's
  // customer-facing text. Administrator catalogue only; absent from every public
  // payload and from an older server, read as "none".
  text_findings: textFindingsSchema,
});
export const publicGuideTitleSchema = z.object({ id: z.string(), title: z.string() });
export type PublicGuideTitle = z.infer<typeof publicGuideTitleSchema>;
export const serviceSummarySchema = z.object({
  id: z.guid(),
  // Internal-only: absent (not merely null) on the narrower public
  // projection a non-admin caller (authenticated internal or anonymous
  // public) receives (`PublicServiceSummary`, backend `catalogue.py`).
  repo_slug: z.string().optional(),
  // The EFFECTIVE display name (override, else extractor-derived, else the
  // repository slug) — present, and carrying the effective value, on every
  // response.
  display_name: z.string(),
  // Internal-only raw override, for the "Administrar" dialog's own editing
  // state; absent on the public projection.
  display_name_override: z.string().nullable().optional(),
  // The effective group (`group_name` verbatim, or absent); present on
  // every response, internal and public.
  group: z.string().nullable(),
  is_public: z.boolean(),
  // Internal-only, same reasoning as `repo_slug` — proposal.md, "Impact":
  // non-admin listings drop both.
  base_path_override: z.string().nullable().optional(),
  operations: z.array(operationSummarySchema),
  guides: z.array(publicGuideTitleSchema).optional(),
  freshness: freshnessField,
});
const exampleSchema = z.object({
  name: z.string(),
  summary: z.string().nullable(),
  value: z.unknown(),
  // The public payload carries examples as {name, summary, value} only.
  provenance: z.string().nullable().optional(),
});
// show-verified-evidence-in-api-reference spec, "The Respuestas section ends with known errors
// from verified evidence": one row per exportable `error` / `response_status` claim, already in
// reading order. A claim with no HTTP status has a null `status`, one with no code a null `code`.
export const knownErrorSchema = z.object({
  status: z.number().int().nullable(),
  code: z.string().nullable(),
  statement: z.string(),
});
export type KnownError = z.infer<typeof knownErrorSchema>;
export const operationDetailSchema = operationSummarySchema.extend({
  description: z.string().nullable(),
  auth_kind: z.string().nullable(),
  operation_json: jsonObject,
  // The public payload drops the provenance map, the commit, the generation time
  // and the operation key (make-public-docs-customer-ready, design §3): all four
  // tolerate being absent.
  provenance_json: jsonObject.optional().default({}),
  schema_resolution: z.object({
    status: z.enum(["complete", "partial"]),
    issues: z.array(
      z.object({
        pointer: z.string(),
        reason: z.enum(["cycle", "depth_limit", "node_limit", "producer_unresolved"]),
      }),
    ),
  }),
  request_examples: z.array(exampleSchema),
  response_examples: z.record(z.string(), z.array(exampleSchema)),
  servers: z.array(jsonObject),
  base_path: z.string().nullable(),
  // make-published-docs-public spec, "The public docs API is open,
  // rate-limited, and indistinguishable for anything unavailable": these
  // three are admin-only and absent (not merely null) on the public
  // payload — present, exactly as today, on the internal one.
  base_path_override: z.string().nullable().optional(),
  source_commit_sha: z.string().nullable().optional(),
  generated_at: z.string().nullable().optional(),
  repo_slug: z.string().optional(),
  operation_key: z.string().optional(),
  extractor: z
    .object({
      version: z.string().nullable(),
      static_routes: z.number().int().nullable(),
      inferred_fields: z.number().int().nullable(),
      unresolved_schemas: z.number().int().nullable(),
    })
    .nullable()
    .optional(),
  freshness: freshnessField,
  // add-api-evidence-dossier spec, requirement 10: the audit's findings against the latest
  // approved evidence dossier. Administrator responses only: null for anyone else, and absent
  // (not merely null) from every public payload and from an older server.
  evidence_findings: z.array(findingViewSchema).nullable().optional(),
  // The verified errors of the operation, public and administrator payloads alike. An older server
  // sends none: read as an empty list, which renders no table.
  known_errors: z.array(knownErrorSchema).default([]),
});
export type ApiOperation = z.infer<typeof operationDetailSchema>;
export type ApiService = z.infer<typeof serviceSummarySchema>;

export class PublicRateLimitError extends Error {
  constructor() {
    super("rate_limited");
    this.name = "PublicRateLimitError";
  }
}
/** api_reference spec, "Loading, empty and error states are distinct and
 * recoverable": thrown by `publicGet` for a `>= 500` response so callers can
 * offer "Reintentar" the same way they do for a rate limit or a network
 * failure, instead of the single indistinguishable unavailability message
 * reserved for anything unknown, non-public, or ineligible. */
export class PublicServerError extends Error {
  constructor() {
    super("public_server_error");
    this.name = "PublicServerError";
  }
}
function isAbortError(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    (error as { name: unknown }).name === "AbortError"
  );
}
/** Discriminates a public query's error into what the page should show:
 * `retryable` (rate limit, server error, network failure) carries the exact
 * copy and pairs with a "Reintentar" control that calls the query's
 * `refetch`; `abort` is a deliberate cancellation and SHALL render nothing
 * new; anything else is the generic, intentionally indistinguishable
 * unavailability outcome (make-published-docs-public spec, "The public docs
 * API is open, rate-limited, and indistinguishable for anything
 * unavailable"). */
export type PublicErrorOutcome =
  | { kind: "retryable"; message: string }
  | { kind: "generic" }
  | { kind: "abort" };
export function classifyPublicError(error: unknown): PublicErrorOutcome {
  if (error instanceof PublicRateLimitError) {
    return {
      kind: "retryable",
      message: "Demasiadas consultas desde esta red. Espera un momento y vuelve a intentarlo.",
    };
  }
  if (error instanceof PublicServerError || error instanceof TypeError) {
    return { kind: "retryable", message: "No se pudo cargar la documentación." };
  }
  if (isAbortError(error)) return { kind: "abort" };
  return { kind: "generic" };
}
export type ApiOperationSummary = z.infer<typeof operationSummarySchema>;
export const apiReferenceKeys = {
  all: ["api-reference"] as const,
  services: ["api-reference", "services"] as const,
  // The quarantine and missing-operation listing, shared by the services
  // dialog's "Cuarentena" tab (and the status drawer's badge).
  quarantine: ["api-reference", "quarantine"] as const,
  operation: (service: string, operation: string) =>
    ["api-reference", "operation", service, operation] as const,
};
/** What a mutation awaits before it settles: every api-reference query except the
 * quarantine listing, which is always active and slow to fail. That one is only
 * marked stale and refetched in the background. */
export function invalidateReference(client: QueryClient): Promise<void> {
  void client.invalidateQueries({ queryKey: apiReferenceKeys.quarantine });
  return client.invalidateQueries({
    queryKey: apiReferenceKeys.all,
    predicate: (query) => query.queryKey[1] !== apiReferenceKeys.quarantine[1],
  });
}
const operationPath = (service: string, operation: string) =>
  `/api-reference/services/${encodeURIComponent(service)}/operations/${encodeURIComponent(operation)}`;
export const fetchApiReferenceServices = () =>
  apiClient.get("/api-reference/services", z.array(serviceSummarySchema));
export function useApiReferenceServices(enabled = true) {
  return useQuery({
    queryKey: apiReferenceKeys.services,
    queryFn: fetchApiReferenceServices,
    enabled,
    retry: false,
    refetchInterval: 60_000,
  });
}
export function useApiReferenceOperation(service: string, operation: string) {
  return useQuery({
    queryKey: apiReferenceKeys.operation(service, operation),
    queryFn: () => apiClient.get(operationPath(service, operation), operationDetailSchema),
    enabled: Boolean(service && operation),
    retry: false,
  });
}
// api_reference spec, "Administrators curate visibility in an explicit
// curation mode": both mutations below update the `services` list
// optimistically (so the checkbox reflects the change before the PATCH
// resolves), roll back to the pre-mutation snapshot on failure, and
// invalidate the whole `api-reference` key group on settle so the operation
// detail's own "Pública/Privada" label picks up the change too.
// make-public-docs-customer-ready, design §5: making a service public succeeds
// and says how many of its operations stay withheld for their text.
const serviceVisibilitySchema = z.object({
  is_public: z.boolean(),
  withheld_operations: z.number().int().default(0),
});
export function useSetServiceVisibility() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ service, isPublic }: { service: string; isPublic: boolean }) =>
      apiClient.patch(
        `/api-reference/services/${encodeURIComponent(service)}`,
        serviceVisibilitySchema,
        { is_public: isPublic },
      ),
    onMutate: async ({ service, isPublic }) => {
      await client.cancelQueries({ queryKey: apiReferenceKeys.services });
      const previous = client.getQueryData<ApiService[]>(apiReferenceKeys.services);
      client.setQueryData<ApiService[]>(apiReferenceKeys.services, (current) =>
        current?.map((item) => (item.id === service ? { ...item, is_public: isPublic } : item)),
      );
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context) client.setQueryData(apiReferenceKeys.services, context.previous);
    },
    onSettled: () => invalidateReference(client),
  });
}
export function useSetOperationVisibility() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      service,
      operation,
      isPublic,
    }: { service: string; operation: string; isPublic: boolean }) =>
      apiClient.patch(operationPath(service, operation), z.object({ is_public: z.boolean() }), {
        is_public: isPublic,
      }),
    onMutate: async ({ service, operation, isPublic }) => {
      await client.cancelQueries({ queryKey: apiReferenceKeys.services });
      const previous = client.getQueryData<ApiService[]>(apiReferenceKeys.services);
      client.setQueryData<ApiService[]>(apiReferenceKeys.services, (current) =>
        current?.map((item) =>
          item.id === service
            ? {
                ...item,
                operations: item.operations.map((op) =>
                  op.operation_id === operation ? { ...op, is_public: isPublic } : op,
                ),
              }
            : item,
        ),
      );
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context) client.setQueryData(apiReferenceKeys.services, context.previous);
    },
    onSettled: () => invalidateReference(client),
  });
}
export const setBasePath = (service: string, value: string | null) =>
  apiClient.patch(
    `/api-reference/services/${encodeURIComponent(service)}/base-path`,
    z.object({ base_path_override: z.string().nullable() }),
    { base_path_override: value },
  );
export const setDisplayName = (service: string, value: string | null) =>
  apiClient.patch(
    `/api-reference/services/${encodeURIComponent(service)}/display-name`,
    z.object({ display_name_override: z.string().nullable() }),
    { display_name: value },
  );
export const setGroup = (service: string, value: string | null) =>
  apiClient.patch(
    `/api-reference/services/${encodeURIComponent(service)}/group`,
    z.object({ group: z.string().nullable() }),
    { group: value },
  );
// A resource-group key of "/" (an operation whose path has no meaningful
// first segment) cannot be addressed by a path segment — uvicorn decodes
// `%2F` before routing, so the encoded path 404s. The key travels in the
// body instead of the URL.
export const setResourceGroupLabel = (service: string, group: string, value: string | null) =>
  apiClient.patch(
    `/api-reference/services/${encodeURIComponent(service)}/resource-group-label`,
    z.object({ label: z.string().nullable() }),
    { resource_group: group, label: value },
  );
export const quarantineSchema = z.array(
  z.object({
    kind: z.string(),
    service_id: z.string(),
    reason: z.string(),
    operation_id: z.string().nullable(),
    observed_at: z.string(),
    // Whether the entry still affects readers: a quarantined spec stays listed
    // after a newer observation supersedes it. Absent on an older server.
    current: z.boolean().default(true),
  }),
);
export const fetchApiQuarantine = () =>
  apiClient.get("/api-reference/quarantine", quarantineSchema);

const publicDocsBase = "/public/docs";
const publicOperationPath = (service: string, operation: string) =>
  `${publicDocsBase}/services/${encodeURIComponent(service)}/operations/${encodeURIComponent(operation)}`;

/** make-published-docs-public spec, "The public docs API is open,
 * rate-limited, and indistinguishable for anything unavailable": no token,
 * no request body — every public read is a plain GET. */
export async function publicGet(path: string, signal?: AbortSignal): Promise<unknown> {
  // "In-app links to the public portal use the configured docs origin": the
  // portal stays same-origin (under `/api`) while it runs on the configured
  // docs host, and otherwise keeps using the configured API base.
  const base = publicApiBase();
  const response = await fetch(`${base}${path}`, {
    method: "GET",
    credentials: "omit",
    cache: "no-store",
    referrerPolicy: "no-referrer",
    signal: signal ?? null,
  });
  if (response.status === 429) throw new PublicRateLimitError();
  if (response.status >= 500) throw new PublicServerError();
  if (!response.ok) throw new Error("No disponible.");
  return response.json();
}
export async function resolvePublicApiReference(signal?: AbortSignal) {
  return z
    .object({ services: z.array(serviceSummarySchema) })
    .parse(await publicGet(`${publicDocsBase}/catalogue`, signal));
}
export async function fetchPublicApiReferenceOperation(
  service: string,
  operation: string,
  signal?: AbortSignal,
) {
  return operationDetailSchema.parse(
    await publicGet(publicOperationPath(service, operation), signal),
  );
}
